<script setup lang="ts">
import { WidgetHeroBannerLayout } from '~/components/widget/hero-banner'
import type { IBankingSummary } from './types'
import { useBankingNumber } from './useBankingNumber'
import { useBankingRegions } from './useBankingRegions'
import WidgetBankingStatsHeroFact from './WidgetBankingStatsHeroFact.vue'

const props = defineProps<{
  summary: IBankingSummary | null
}>()

const { t } = useI18n()
const { formatDate } = useBankingNumber()

const asOf = computed(() => props.summary?.as_of ?? null)
const importedAt = computed(() => props.summary?.imported_at ?? null)

/** "Источник: НБКР, данные на 31.08.2026, обновлено 29.10.2026"; each date only when known. */
const sourceLine = computed(() => {
  const source = asOf.value
    ? t('banking_statistics.source_as_of', { date: formatDate(asOf.value) })
    : t('banking_statistics.source')

  return importedAt.value
    ? `${source}, ${t('banking_statistics.updated_at', { date: formatDate(importedAt.value) })}`
    : source
})
// Text from the CMS SEO settings, as on other section pages; the page's own text is a fallback
const nuxtApp = useNuxtApp()
const description = computed(
  () => nuxtApp._seoData?.value?.upper_description || t('banking_statistics.description')
)

// The hero shows the fact of the month; assets, loans and deposits stay in the KPI cards below (#515)
const { story } = useBankingRegions()

const banksCount = computed(() => props.summary?.banks_count?.value ?? null)
const branchesCount = computed(() => props.summary?.branches_count?.value ?? null)
</script>

<template>
  <WidgetHeroBannerLayout width="wide" backdrop="waves" data-testid="banking-header">
    <Heading tag="h1">{{ t('banking_statistics.title') }}</Heading>
    <div
      v-hydrated-html="description"
      class="max-w-2xl space-y-3 leading-6 text-gray-700 md:leading-7 [&_b]:font-semibold [&_b]:text-brand-gray [&_strong]:font-semibold [&_strong]:text-brand-gray"
    />
    <div class="flex flex-wrap gap-2">
      <span v-if="banksCount !== null" class="rounded-full bg-white px-3.5 py-1.5 text-sm ring-1 ring-blue-100">
        {{ t('banking_statistics.banks') }}: <b>{{ banksCount }}</b>
      </span>
      <span v-if="branchesCount !== null" class="rounded-full bg-white px-3.5 py-1.5 text-sm ring-1 ring-blue-100">
        {{ t('banking_statistics.branches') }}: <b>{{ branchesCount }}</b>
      </span>
    </div>
    <p class="text-sm text-gray-500">{{ sourceLine }}</p>

    <WidgetBankingStatsHeroFact v-if="story" :story="story" compact class="lg:hidden" />

    <template v-if="story" #illustration>
      <WidgetBankingStatsHeroFact :story="story" />
    </template>
  </WidgetHeroBannerLayout>
</template>
